import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  message,
  Popconfirm,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
  Tooltip,
  Tag
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  EyeOutlined,
  StarOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { servicesService } from "../../services/servicesService";

import { useNavigate } from "react-router-dom";


const { Title, Text } = Typography;
const { TextArea } = Input;

function AddServices(props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [services, setServices] = useState([]);
  // Removed local dark mode, now handled globally
  const navigate = useNavigate();
  // Theme toggle handled globally

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await servicesService.getAllServices();
      setServices(data);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values) => {
    try {
      const serviceData = {
        name: values.title,
        description: values.description,
      };
      await servicesService.createService(serviceData);
      message.success('Service added successfully');
      fetchData();
      form.resetFields();
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.data || 'Failed to add service');
      } else {
        message.error('Failed to add service');
      }
    }
  };

  const handleEdit = (record) => {
    setEditingService(record);
    form.setFieldsValue({
      title: record.name,
      description: record.description
    });
    setModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const serviceData = {
        name: values.title,
        description: values.description,
      };
      await servicesService.updateService(editingService._id, serviceData);
      message.success('Service updated successfully');
      setModalVisible(false);
      setEditingService(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error('Failed to update service');
    }
  };

  const handleDelete = async (id) => {
    try {
      await servicesService.deleteService(id);
      message.success('Service deleted successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to delete service');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingService(null);
    form.resetFields();
  };

  // Statistics
  const stats = {
    total: services.length,
    active: services.filter(service => service.name && service.description).length
  };

  // Table columns
  const columns = [
    {
      title: 'Leistungsname',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Text strong style={{ fontSize: '16px' }}>
          {name || 'Unbenannte Leistung'}
        </Text>
      ),
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
       ellipsis: true,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {description && description.length > 100 ? `${description.substring(0, 100)}...` : description || 'Keine Beschreibung'}
        </Text>
      ),
       ellipsis: true,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Erstellt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString('de-DE') : '',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      ellipsis: true,
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Details anzeigen">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: record.name || 'Unbenannte Leistung',
                  content: (
                    <div style={{ padding: 12, background: 'var(--app-bg, #f5f5f5)', borderRadius: 6 }}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {record.description || 'Keine Beschreibung vorhanden'}
                      </div>
                    </div>
                  ),
                  width: 500
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Möchten Sie diese Leistung wirklich löschen?"
            onConfirm={() => handleDelete(record._id)}
            okText="Ja"
            cancelText="Nein"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Tooltip title="Löschen">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div
      style={{ padding: '24px', background: 'var(--app-bg, #f5f5f5)', minHeight: '100vh', maxWidth: '100vw', margin: '0 auto' }}
    >
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Leistungsverwaltung
            </Title>
            <Text type="secondary">Verwalten Sie die Leistungen und Behandlungen Ihrer Praxis</Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setModalVisible(true)}
            >
              Leistung hinzufügen
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title="Gesamtanzahl Leistungen"
              value={stats.total}
              prefix={<SettingOutlined />}
              valueStyle={{ color: '#41b06e' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="Aktive Leistungen"
              value={stats.active}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Services Table */}
      <Card >
        <Spin spinning={loading}>
          {services.length === 0 ? (
            <Empty
              description="Keine Leistungen gefunden"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => setModalVisible(true)}>
                Erste Leistung hinzufügen
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={services}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} von ${total} Einträgen`
              }}
              style={{ width: '100%' }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingService ? 'Leistung bearbeiten' : 'Neue Leistung hinzufügen'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnHidden 
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingService ? handleUpdate : handleSubmit}
        >
          <Form.Item
            name="title"
            label="Leistungsname"
            rules={[{ required: true, message: 'Bitte geben Sie den Leistungsnamen ein' }]}
          >
            <Input placeholder="Leistungsname eingeben" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Beschreibung"
            rules={[{ required: true, message: 'Bitte geben Sie eine Beschreibung ein' }]}
          >
            <TextArea
              rows={6}
              placeholder="Leistungsbeschreibung eingeben"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Abbrechen
              </Button>
              <Button type="primary" htmlType="submit">
                {editingService ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AddServices;
